import moment from 'moment-timezone'

const scheduleRule = (schedule, date) => {
  const weekday = date.locale('en').format('dddd').toLowerCase()

  if (!schedule) {
    return { type: 'wday', wday: weekday, intervals: [], timezone: 'America/New_York' }
  }

  const dateRule = schedule.rules?.find((rule) => rule.type === 'date' && rule.date === date.format('YYYY-MM-DD'))
  if (dateRule) {
    return { ...dateRule, timezone: schedule.timezone }
  }

  const wdayRule = schedule.rules?.find((rule) => rule.type === 'wday' && rule.wday === weekday)
  if (wdayRule) {
    return { ...wdayRule, timezone: schedule.timezone }
  }

  return { type: 'wday', wday: weekday, intervals: [], timezone: schedule.timezone }
}

const alignInterval = (interval, date, scheduleTimezone, targetTimezone, currentTime) => {
  const dateIntervals = {}
  const aligned = {
    from: moment.tz(`${date.format('YYYY-MM-DD')} ${interval.from}`, scheduleTimezone).tz(targetTimezone),
    to: moment.tz(`${date.format('YYYY-MM-DD')} ${interval.to}`, scheduleTimezone).tz(targetTimezone)
  }

  if (currentTime) {
    // Skip if before current time
    if (aligned.to.isBefore(currentTime)) {
      return {}
    // Disable intervals before current time
    } if (aligned.from.isBefore(currentTime)) {
      aligned.from = currentTime
    }
  }

  if (aligned.from.dayOfYear() < aligned.to.dayOfYear()) {
    dateIntervals[aligned.from.format('YYYY-MM-DD')] = [{ from: aligned.from.format('HH:mm'), to: '24:00' }]
    dateIntervals[aligned.to.format('YYYY-MM-DD')] = [{ from: '00:00', to: aligned.to.format('HH:mm') }]
  } else {
    dateIntervals[aligned.from.format('YYYY-MM-DD')] = [{ from: aligned.from.format('HH:mm'), to: aligned.to.format('HH:mm') }]
  }

  return dateIntervals
}

const alignSchedules = (schedules, startDate, endDate, targetTimezone, currentTime) => schedules.map((schedule) => {
  const alignedSchedule = {}
  const indexDate = startDate.clone().subtract(1, 'day')

  while (indexDate.isSameOrBefore(endDate)) {
    alignedSchedule[indexDate.format('YYYY-MM-DD')] = alignedSchedule[indexDate.format('YYYY-MM-DD')] || []
    const rule = scheduleRule(schedule, indexDate)
    if (rule) {
      (rule.intervals || []).forEach((originalInterval) => {
        const intervalPerDates = alignInterval(originalInterval, indexDate, rule.timezone, targetTimezone, currentTime)
        Object.entries(intervalPerDates).forEach(([date, intervals]) => {
          alignedSchedule[date] = alignedSchedule[date] || []
          alignedSchedule[date].push(...intervals)
        })
      })
    }
    indexDate.add(1, 'day')
  }
  return alignedSchedule
})

const calculateIntersection = (availability, intervals) => {
  const intersections = []
  if (!intervals || !intervals.length) { return intersections }

  intervals.forEach((interval) => {
    availability.forEach((spot) => {
      const [min, max] = spot.from < interval.from ? [spot, interval] : [interval, spot]
      if (min.to >= max.from) {
        const newTo = min.to < max.to ? min.to : max.to
        if (max.from !== newTo) {
          intersections.push({ from: max.from, to: newTo })
        }
      }
    })
  })
  return intersections.sort((firstInterval, secondInterval) => (firstInterval.from > secondInterval.from ? 1 : -1))
}

const calculateAvailabilityForDate = (schedules, date) => {
  let availability = [...schedules[0][date.format('YYYY-MM-DD')]]
  schedules.slice(1).forEach((schedule) => {
    const intervals = schedule[date.format('YYYY-MM-DD')]
    availability = calculateIntersection(availability, intervals)
  })
  return availability
}


const availabilityForDateRange = (schedules, targetTimezone, startDate, endDate, currentTime) => {
  const indexDate = startDate.clone()
  const availabilityPerDate = {}
  const alignedSchedules = alignSchedules(schedules, startDate, endDate, targetTimezone, currentTime)
  while (indexDate.isSameOrBefore(endDate)) {
    const availability = calculateAvailabilityForDate(alignedSchedules, indexDate)
    availabilityPerDate[indexDate.format('YYYY-MM-DD')] = availability
    indexDate.add(1, 'day')
  }
  return availabilityPerDate
}

const invertAvailability = (availabilityIntervals) => {
  let intervalStart = '00:00'
  const invertedIntervals = []
  availabilityIntervals.forEach((interval) => {
    if (intervalStart !== interval.from) {
      invertedIntervals.push({ from: intervalStart, to: interval.from })
    }
    intervalStart = interval.to
  })
  if (intervalStart !== '24:00') {
    invertedIntervals.push({ from: intervalStart, to: '24:00' })
  }

  return invertedIntervals
}

export const unavailabilityForDateRange = (schedules, targetTimezone, startDate, endDate, currentTime) => {
  const availability = availabilityForDateRange(schedules, targetTimezone, startDate, endDate, currentTime)
  const unavailability = {}
  Object.entries(availability).forEach(([date, availabilityIntervals]) => {
    unavailability[date] = invertAvailability(availabilityIntervals)
  })

  return unavailability
}
